import { useQuery } from 'react-query';

import { ROOT_URL } from '@/constants/configConstants';
import api, { QueryConfig } from '@shared/api';
import useActiveWorkspace from '@shared/hooks/workspace/useActiveWorkspace';
import useComputeEngineURL from './useComputeEngineURL';
import { useModelsToken } from './useModelsToken';

export const getURLOverride = (): string => {
  const { origin } = new URL(ROOT_URL || '', window.location.origin);

  return `${origin}/clientlib`;
};

const GET_PYTHON_PANEL_URL = '/api/get-python-panel-url';

type UsePythonPanelURLParams = {
  code?: string;
  instanceId: string;
  options?: Record<string, unknown>;
  panelHeight?: number;
  panelWidth?: number;
  workspaceName?: string;
};

interface GetPythonPanelURLParams extends UsePythonPanelURLParams {
  code: string;
  computeEngineURL: string;
  token: string;
  workspaceName: string;
}

interface GetUsePythonPanelURLResponse {
  url: string;
}

async function getPythonPanelURL(
  {
    code,
    computeEngineURL,
    instanceId,
    options,
    panelHeight,
    panelWidth,
    token,
    workspaceName
  }: GetPythonPanelURLParams,
  signal: AbortSignal | undefined
): Promise<string> {
  const urlOverride = getURLOverride();

  const { data } = await api.post<GetUsePythonPanelURLResponse>(
    `${computeEngineURL}${GET_PYTHON_PANEL_URL}`,
    {
      code,
      height: panelHeight,
      instanceId,
      options,
      url_override: urlOverride,
      width: panelWidth,
      workspace: workspaceName
    },
    { signal, headers: { 'X-API-KEY': token } }
  );

  if (!data?.url) {
    throw new Error('No URL returned from compute engine');
  }

  return data?.url + '?embed=true';
}

export function usePythonPanelURL(
  {
    code,
    instanceId,
    options,
    panelHeight,
    panelWidth,
    workspaceName: paramWorkspaceName
  }: UsePythonPanelURLParams,
  config?: QueryConfig<string>
) {
  const workspace = useActiveWorkspace();
  const { data: computeEngineURL } = useComputeEngineURL({
    workspaceId: workspace.id
  });
  const { data: token } = useModelsToken({ workspaceId: workspace.id });
  const workspaceName = paramWorkspaceName || workspace.name;

  return useQuery(
    [
      'mpm-python-panel-url',
      {
        code,
        computeEngineURL,
        instanceId,
        options,
        panelWidth,
        panelHeight,
        token,
        workspaceName
      }
    ],
    async ({ signal }) => {
      if (!token || !computeEngineURL || !code) return '';

      return getPythonPanelURL(
        {
          code,
          computeEngineURL,
          instanceId,
          options,
          panelHeight,
          panelWidth,
          token,
          workspaceName
        },
        signal
      );
    },
    {
      ...config,
      enabled:
        (config?.enabled ?? true) && !!code && !!computeEngineURL && !!token
    }
  );
}
